import React, { useState, useEffect, useRef } from 'react';

const AccessibilityTester = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');
  const [focusOrder, setFocusOrder] = useState([]);
  const [contrastResults, setContrastResults] = useState([]);
  const [ariaResults, setAriaResults] = useState([]);
  const [keyboardResults, setKeyboardResults] = useState([]);

  const addTestResult = (testName, status, details = '', category = 'general') => {
    const result = {
      id: Date.now() + Math.random(),
      testName,
      status, // 'pass', 'fail', 'warning', 'info'
      details,
      category,
      timestamp: new Date().toISOString()
    };
    setTestResults(prev => [...prev, result]);
  };

  const clearResults = () => {
    setTestResults([]);
    setFocusOrder([]);
    setContrastResults([]);
    setAriaResults([]);
    setKeyboardResults([]);
  };

  // Test 1: Color Contrast Analysis
  const testColorContrast = async () => {
    setCurrentTest('Color Contrast Analysis');
    
    try {
      const elements = document.querySelectorAll('*');
      const contrastIssues = [];
      
      elements.forEach(element => {
        const computedStyle = window.getComputedStyle(element);
        const color = computedStyle.color;
        const backgroundColor = computedStyle.backgroundColor;
        
        if (color && backgroundColor && color !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
          const contrast = calculateContrast(color, backgroundColor);
          const fontSize = parseFloat(computedStyle.fontSize);
          const fontWeight = computedStyle.fontWeight;
          
          // WCAG AA requirements
          const isLargeText = fontSize >= 18 || (fontSize >= 14 && (fontWeight === 'bold' || parseInt(fontWeight) >= 700));
          const requiredContrast = isLargeText ? 3 : 4.5;
          
          if (contrast < requiredContrast) {
            contrastIssues.push({
              element: element.tagName,
              contrast: contrast.toFixed(2),
              required: requiredContrast,
              color,
              backgroundColor,
              fontSize,
              fontWeight
            });
          }
        }
      });
      
      if (contrastIssues.length === 0) {
        addTestResult('Color Contrast', 'pass', 'All text meets WCAG AA contrast requirements', 'contrast');
      } else {
        addTestResult('Color Contrast', 'warning', `Found ${contrastIssues.length} contrast issues`, 'contrast');
        setContrastResults(contrastIssues);
      }
    } catch (error) {
      addTestResult('Color Contrast', 'fail', `Error: ${error.message}`, 'contrast');
    }
  };

  // Calculate color contrast ratio
  const calculateContrast = (color1, color2) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    
    if (!rgb1 || !rgb2) return 0;
    
    const lum1 = getLuminance(rgb1);
    const lum2 = getLuminance(rgb2);
    
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    
    return (brightest + 0.05) / (darkest + 0.05);
  };

  const hexToRgb = (color) => {
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      return matches ? {
        r: parseInt(matches[0]),
        g: parseInt(matches[1]),
        b: parseInt(matches[2])
      } : null;
    }
    return null;
  };

  const getLuminance = (rgb) => {
    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  // Test 2: ARIA Labels and Roles
  const testARIALabels = async () => {
    setCurrentTest('ARIA Labels and Roles');
    
    try {
      const interactiveElements = document.querySelectorAll('button, input, select, textarea, a[href], [role="button"], [tabindex]');
      const ariaIssues = [];
      
      interactiveElements.forEach(element => {
        const issues = [];
        
        // Check for accessible name
        const accessibleName = getAccessibleName(element);
        if (!accessibleName) {
          issues.push('Missing accessible name');
        }
        
        // Check for proper ARIA attributes
        if (element.tagName === 'BUTTON' && !element.getAttribute('aria-label') && !element.textContent.trim()) {
          issues.push('Button without text needs aria-label');
        }
        
        // Check for form labels
        if (element.tagName === 'INPUT' && !element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
          const label = document.querySelector(`label[for="${element.id}"]`);
          if (!label) {
            issues.push('Input without associated label');
          }
        }
        
        // Check for proper roles
        if (element.getAttribute('role') && !isValidRole(element.getAttribute('role'))) {
          issues.push(`Invalid ARIA role: ${element.getAttribute('role')}`);
        }
        
        if (issues.length > 0) {
          ariaIssues.push({
            element: element.tagName,
            id: element.id,
            issues
          });
        }
      });
      
      if (ariaIssues.length === 0) {
        addTestResult('ARIA Labels', 'pass', 'All interactive elements have proper ARIA labels', 'aria');
      } else {
        addTestResult('ARIA Labels', 'warning', `Found ${ariaIssues.length} ARIA issues`, 'aria');
        setAriaResults(ariaIssues);
      }
    } catch (error) {
      addTestResult('ARIA Labels', 'fail', `Error: ${error.message}`, 'aria');
    }
  };

  const getAccessibleName = (element) => {
    return element.getAttribute('aria-label') ||
           element.getAttribute('aria-labelledby') ||
           element.textContent?.trim() ||
           element.getAttribute('title') ||
           element.getAttribute('alt') ||
           '';
  };

  const isValidRole = (role) => {
    const validRoles = [
      'button', 'link', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
      'option', 'tab', 'tabpanel', 'textbox', 'checkbox', 'radio',
      'slider', 'spinbutton', 'switch', 'combobox', 'listbox',
      'grid', 'gridcell', 'row', 'rowgroup', 'columnheader',
      'rowheader', 'table', 'cell', 'columnheader', 'rowheader',
      'alert', 'alertdialog', 'dialog', 'log', 'marquee', 'status',
      'timer', 'tooltip', 'tree', 'treeitem', 'treegrid'
    ];
    return validRoles.includes(role);
  };

  // Test 3: Keyboard Navigation
  const testKeyboardNavigation = async () => {
    setCurrentTest('Keyboard Navigation');
    
    try {
      const focusableElements = document.querySelectorAll(
        'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"]), [role="button"]'
      );
      
      const keyboardIssues = [];
      const focusOrder = [];
      
      // Test tab order
      focusableElements.forEach((element, index) => {
        element.addEventListener('focus', () => {
          focusOrder.push({
            index,
            element: element.tagName,
            id: element.id,
            text: element.textContent?.trim() || element.value || element.placeholder || 'No text'
          });
        });
      });
      
      // Test keyboard interactions
      focusableElements.forEach(element => {
        const issues = [];
        
        // Check if element is focusable
        if (element.tabIndex < 0 && !element.getAttribute('aria-hidden')) {
          issues.push('Element not focusable via keyboard');
        }
        
        // Check for visible focus indicators
        element.addEventListener('focus', () => {
          const computedStyle = window.getComputedStyle(element, ':focus');
          const outline = computedStyle.outline;
          const boxShadow = computedStyle.boxShadow;
          
          if (outline === 'none' && !boxShadow) {
            issues.push('No visible focus indicator');
          }
        });
        
        // Check for keyboard event handlers
        if (element.tagName === 'DIV' && element.getAttribute('role') === 'button') {
          if (!element.getAttribute('tabindex')) {
            issues.push('Div with button role needs tabindex');
          }
        }
        
        if (issues.length > 0) {
          keyboardIssues.push({
            element: element.tagName,
            id: element.id,
            issues
          });
        }
      });
      
      if (keyboardIssues.length === 0) {
        addTestResult('Keyboard Navigation', 'pass', 'All elements are properly keyboard accessible', 'keyboard');
      } else {
        addTestResult('Keyboard Navigation', 'warning', `Found ${keyboardIssues.length} keyboard issues`, 'keyboard');
        setKeyboardResults(keyboardIssues);
      }
      
      setFocusOrder(focusOrder);
    } catch (error) {
      addTestResult('Keyboard Navigation', 'fail', `Error: ${error.message}`, 'keyboard');
    }
  };

  // Test 4: Heading Structure
  const testHeadingStructure = async () => {
    setCurrentTest('Heading Structure');
    
    try {
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      const headingStructure = [];
      let hasH1 = false;
      let previousLevel = 0;
      const issues = [];
      
      headings.forEach(heading => {
        const level = parseInt(heading.tagName.charAt(1));
        headingStructure.push({
          level,
          text: heading.textContent?.trim(),
          id: heading.id
        });
        
        if (level === 1) hasH1 = true;
        
        if (level > previousLevel + 1) {
          issues.push(`Heading level skipped: ${heading.tagName} after h${previousLevel}`);
        }
        
        previousLevel = level;
      });
      
      if (!hasH1) {
        issues.push('No h1 heading found');
      }
      
      if (issues.length === 0) {
        addTestResult('Heading Structure', 'pass', 'Proper heading hierarchy found', 'structure');
      } else {
        addTestResult('Heading Structure', 'warning', `Found ${issues.length} heading issues`, 'structure');
      }
    } catch (error) {
      addTestResult('Heading Structure', 'fail', `Error: ${error.message}`, 'structure');
    }
  };

  // Test 5: Form Accessibility
  const testFormAccessibility = async () => {
    setCurrentTest('Form Accessibility');
    
    try {
      const forms = document.querySelectorAll('form');
      const formIssues = [];
      
      forms.forEach(form => {
        const inputs = form.querySelectorAll('input, select, textarea');
        const issues = [];
        
        inputs.forEach(input => {
          // Check for labels
          const label = document.querySelector(`label[for="${input.id}"]`);
          const ariaLabel = input.getAttribute('aria-label');
          const ariaLabelledBy = input.getAttribute('aria-labelledby');
          
          if (!label && !ariaLabel && !ariaLabelledBy) {
            issues.push(`Input ${input.type || input.tagName} missing label`);
          }
          
          // Check for required fields
          if (input.hasAttribute('required') && !input.getAttribute('aria-required')) {
            issues.push(`Required input missing aria-required`);
          }
          
          // Check for error messages
          if (input.hasAttribute('aria-invalid') && input.getAttribute('aria-invalid') === 'true') {
            const errorId = input.getAttribute('aria-describedby');
            if (!errorId || !document.getElementById(errorId)) {
              issues.push(`Input with error missing error message`);
            }
          }
        });
        
        if (issues.length > 0) {
          formIssues.push({
            form: form.id || 'unnamed form',
            issues
          });
        }
      });
      
      if (formIssues.length === 0) {
        addTestResult('Form Accessibility', 'pass', 'All forms are properly accessible', 'forms');
      } else {
        addTestResult('Form Accessibility', 'warning', `Found ${formIssues.length} form issues`, 'forms');
      }
    } catch (error) {
      addTestResult('Form Accessibility', 'fail', `Error: ${error.message}`, 'forms');
    }
  };

  // Test 6: Image Alt Text
  const testImageAltText = async () => {
    setCurrentTest('Image Alt Text');
    
    try {
      const images = document.querySelectorAll('img');
      const imageIssues = [];
      
      images.forEach(img => {
        const alt = img.getAttribute('alt');
        const src = img.getAttribute('src');
        
        if (!alt && !img.getAttribute('aria-hidden')) {
          imageIssues.push({
            src: src || 'unknown',
            issue: 'Missing alt text'
          });
        } else if (alt === '' && !img.getAttribute('aria-hidden')) {
          // Empty alt is acceptable for decorative images
          if (!img.getAttribute('role') || img.getAttribute('role') !== 'presentation') {
            imageIssues.push({
              src: src || 'unknown',
              issue: 'Empty alt text should have role="presentation"'
            });
          }
        }
      });
      
      if (imageIssues.length === 0) {
        addTestResult('Image Alt Text', 'pass', 'All images have proper alt text', 'images');
      } else {
        addTestResult('Image Alt Text', 'warning', `Found ${imageIssues.length} image issues`, 'images');
      }
    } catch (error) {
      addTestResult('Image Alt Text', 'fail', `Error: ${error.message}`, 'images');
    }
  };

  // Test 7: Screen Reader Compatibility
  const testScreenReaderCompatibility = async () => {
    setCurrentTest('Screen Reader Compatibility');
    
    try {
      // Test for proper semantic HTML
      const semanticElements = document.querySelectorAll('main, nav, section, article, aside, header, footer');
      const hasMain = document.querySelector('main');
      const hasNav = document.querySelector('nav');
      
      if (!hasMain) {
        addTestResult('Screen Reader Compatibility', 'warning', 'No main landmark found', 'screenreader');
      } else {
        addTestResult('Screen Reader Compatibility', 'pass', 'Main landmark found', 'screenreader');
      }
      
      if (!hasNav) {
        addTestResult('Screen Reader Compatibility', 'warning', 'No navigation landmark found', 'screenreader');
      } else {
        addTestResult('Screen Reader Compatibility', 'pass', 'Navigation landmark found', 'screenreader');
      }
      
      // Test for skip links
      const skipLinks = document.querySelectorAll('a[href="#main"], a[href="#content"], .skip-link');
      if (skipLinks.length === 0) {
        addTestResult('Screen Reader Compatibility', 'warning', 'No skip links found', 'screenreader');
      } else {
        addTestResult('Screen Reader Compatibility', 'pass', 'Skip links found', 'screenreader');
      }
      
    } catch (error) {
      addTestResult('Screen Reader Compatibility', 'fail', `Error: ${error.message}`, 'screenreader');
    }
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunning(true);
    clearResults();
    
    const tests = [
      testColorContrast,
      testARIALabels,
      testKeyboardNavigation,
      testHeadingStructure,
      testFormAccessibility,
      testImageAltText,
      testScreenReaderCompatibility
    ];

    for (const test of tests) {
      try {
        await test();
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        addTestResult('Test Execution', 'fail', `Test failed: ${error.message}`);
      }
    }

    setIsRunning(false);
    setCurrentTest('');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-100';
      case 'fail': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✓';
      case 'fail': return '✗';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return '?';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Accessibility Testing Suite</h1>
        
        {/* Test Controls */}
        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isRunning ? 'Running Tests...' : 'Run All Tests'}
            </button>
            
            <button
              onClick={clearResults}
              disabled={isRunning}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Clear Results
            </button>
          </div>
          
          {isRunning && (
            <div className="text-blue-600 font-medium">
              Currently running: {currentTest}
            </div>
          )}
        </div>

        {/* Test Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Results</h2>
          
          {testResults.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No tests run yet. Click "Run All Tests" to start testing accessibility.
            </div>
          ) : (
            <div className="space-y-3">
              {testResults.map((result) => (
                <div key={result.id} className="flex items-start p-4 border rounded-lg">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${getStatusColor(result.status)}`}>
                    {getStatusIcon(result.status)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{result.testName}</div>
                    <div className="text-sm text-gray-600 mt-1">{result.details}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {result.category} • {new Date(result.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detailed Results */}
        {contrastResults.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-semibold text-yellow-900 mb-2">Color Contrast Issues</h3>
            <div className="space-y-2 text-sm">
              {contrastResults.map((issue, index) => (
                <div key={index} className="text-yellow-800">
                  <strong>{issue.element}:</strong> Contrast ratio {issue.contrast} (required: {issue.required})
                </div>
              ))}
            </div>
          </div>
        )}

        {ariaResults.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-semibold text-yellow-900 mb-2">ARIA Issues</h3>
            <div className="space-y-2 text-sm">
              {ariaResults.map((issue, index) => (
                <div key={index} className="text-yellow-800">
                  <strong>{issue.element} {issue.id}:</strong> {issue.issues.join(', ')}
                </div>
              ))}
            </div>
          </div>
        )}

        {keyboardResults.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-semibold text-yellow-900 mb-2">Keyboard Navigation Issues</h3>
            <div className="space-y-2 text-sm">
              {keyboardResults.map((issue, index) => (
                <div key={index} className="text-yellow-800">
                  <strong>{issue.element} {issue.id}:</strong> {issue.issues.join(', ')}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {testResults.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Test Summary</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-medium">
                  Passed: {testResults.filter(r => r.status === 'pass').length}
                </span>
              </div>
              <div>
                <span className="text-red-600 font-medium">
                  Failed: {testResults.filter(r => r.status === 'fail').length}
                </span>
              </div>
              <div>
                <span className="text-yellow-600 font-medium">
                  Warnings: {testResults.filter(r => r.status === 'warning').length}
                </span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">
                  Info: {testResults.filter(r => r.status === 'info').length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Accessibility Guidelines */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">WCAG 2.1 Guidelines</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• <strong>Perceivable:</strong> Information must be presentable in ways users can perceive</p>
            <p>• <strong>Operable:</strong> Interface components must be operable by all users</p>
            <p>• <strong>Understandable:</strong> Information and UI operation must be understandable</p>
            <p>• <strong>Robust:</strong> Content must be robust enough for assistive technologies</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityTester;
